/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getOffset from '@stdlib/ndarray/base/offset';
import getData from '@stdlib/ndarray/base/data-buffer';
import ndarraylike2scalar from '@stdlib/ndarray/base/ndarraylike2scalar';
import { ndarray as strided } from './../../../../strided/dmeanstdev';


// MAIN //

/**
* Computes the mean and standard deviation of a one-dimensional double-precision floating-point ndarray.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   a one-dimensional input ndarray.
*     -   a one-dimensional output ndarray.
*     -   a zero-dimensional ndarray specifying the degrees of freedom adjustment.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @returns {ndarrayLike} output ndarray
*
* @example
* import Float64Vector from '@stdlib/ndarray/vector/float64';
* import scalar2ndarray from '@stdlib/ndarray/from-scalar';
*
* var opts = {
*     'dtype': 'float64'
* };
*
* var x = new Float64Vector( [ 1.0, 3.0, 4.0, 2.0 ] );
* var out = new Float64Vector( 2 );
*
* var correction = scalar2ndarray( 1.0, opts );
*
* var v = dmeanstdev( [ x, out, correction ] );
* // returns <ndarray>[ 2.5, ~1.2910 ]
*/
function dmeanstdev( arrays ) {
	var correction;
	var out;
	var x;

	x = arrays[ 0 ];
	out = arrays[ 1 ];
	correction = ndarraylike2scalar( arrays[ 2 ] );

	strided( numelDimension( x, 0 ), correction, getData( x ), getStride( x, 0 ), getOffset( x ), getData( out ), getStride( out, 0 ), getOffset( out ) ); // eslint-disable-line max-len

	return out;
}


// EXPORTS //

export default dmeanstdev;
